import { Experiment } from '@ms/yammer-data/dist/state/types';
import { useCanCurrentUserAccessAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useAndReportExperimentTreatment } from '@ms/yammer-hooks/dist/experiments';
import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganization } from '@ms/yammer-hooks/dist/multiTenantOrganization';
import { usePushSubscription } from '@ms/yammer-hooks/dist/pushSubscriptions';
import { useServiceWorkerMessages } from '@ms/yammer-hooks/dist/pwa';
import { useLoadStorylineSettings } from '@ms/yammer-hooks/dist/storylineSettings';
import { useSendPresenceState } from '@ms/yammer-hooks/dist/userPresence';
import { useCurrentLocale } from '@ms/yammer-i18n-react';
import { usePersistLocale } from '@ms/yammer-i18n-react/dist/hooks';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import Skiplink from '@ms/yammer-web-components/dist/accessibility/Skiplink';
import AppContextProvider from '@ms/yammer-web-components/dist/app/AppContext';
import AppDocumentHeadOverrides from '@ms/yammer-web-components/dist/app/AppDocumentHeadOverrides';
import ArticleScrollProvider from '@ms/yammer-web-components/dist/articles/ArticleScrollContext';
import { LinkRenderingOptions, LinkRenderingOptionsProvider } from '@ms/yammer-web-components/dist/link/YammerLink';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import RealtimeClient from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { PageLayoutContentMainAreaAriaRole } from '@ms/yammer-web-core-ui/dist/components/PageLayout';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { useMainFeatureSettings } from '@ms/yammer-web-ui-features/dist/featureSettings';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import React, { FC } from 'react';

import Dialogs from '../Dialogs';
import Routes from '../Routes';

import { useThemeWithSuiteHeaderOverrides } from './hooks';

const linkRenderingOptions: LinkRenderingOptions = {
  shouldOpenArticleViewLinkInNewWindow: true,
};

const App: FC = () => {
  usePersistLocale();
  useServiceWorkerMessages();
  usePushSubscription();
  useSendPresenceState();
  usePrepareIcons();
  useLoadStorylineSettings();
  useLoadMultiTenantOrganization();
  useLoadFeatureAccessInsights({ vivaEngagePremiumOnly: true });
  useAndReportExperimentTreatment(Experiment.VivaEngageRebranding);

  const features = useMainFeatureSettings({
    overrides: {
      navigation: {
        admin: useCanCurrentUserAccessAdminSettings(),
      },
    },
  });
  const theme = useThemeWithSuiteHeaderOverrides();
  const locale = useCurrentLocale();

  return (
    <AppContainer theme={theme} locale={locale}>
      <UIFeaturesProvider features={features}>
        <SessionContextProviderWithHooks>
          <AppContextProvider mainAreaAriaRole={PageLayoutContentMainAreaAriaRole.Main}>
            <LinkRenderingOptionsProvider options={linkRenderingOptions}>
              <ArticleScrollProvider>
                <AppDocumentHeadOverrides />
                <Skiplink />
                <Routes />
                <Dialogs />
                <MediaLightbox />
                <RealtimeClient />
                <ReduxAnnouncerBridge />
              </ArticleScrollProvider>
            </LinkRenderingOptionsProvider>
          </AppContextProvider>
        </SessionContextProviderWithHooks>
      </UIFeaturesProvider>
    </AppContainer>
  );
};
export default App;
